import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcDtccDtcV1AssetDayQueryServiceAssetDayGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DateFormat } from '@interxfi/design-system/components/format/date';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcDtccDtcV1AssetDayState } from '@interxfi/types';
import { AssetBreadcrumb } from '../../../../../../../../../../../features/asset/asset-breadcrumb';
import { AssetLogoBreadcrumb } from '../../../../../../../../../../../features/asset/logo/asset-logo-breadcrumb';
import { DtcAssetDayEventDrawer, DtcAssetDayEventSummary } from '../../../../../../../../../../../features/dtcc/dtc-asset-day/dtc-asset-day-event';
import { getDtcAssetDayStateListItems } from '../../../../../../../../../../../features/dtcc/dtc-asset-day/dtc-asset-day-list-items';
import { getDtcAssetDayDetailPath } from '../../../../../../../../../../../features/dtcc/dtc-asset-day/dtc-asset-day-routing';
import { DtcAssetDayStatusPill } from '../../../../../../../../../../../features/dtcc/dtc-asset-day/dtc-asset-day-status-pill';
import { DtcCaPaymentsTable } from '../../../../../../../../../../../features/dtcc/dtc-asset-day/dtc-ca-payments-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/dtcc/asset/_asset/$assetId/$date')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `dtc-asset-day-${params.assetId}-${params.date}`,
        label: (
          <AssetBreadcrumb
            assetId={params.assetId}
            suffix={
              <span>
                (<DateFormat value={params.date} wellKnownType="date_iso" />)
              </span>
            }
          />
        ),
        prefix: <AssetLogoBreadcrumb assetId={params.assetId} />,
        to: getDtcAssetDayDetailPath(params.assetId, params.date),
        suffix: <CopyButton content={params.assetId} label="Copy Asset ID" tooltip={{ copy: params.assetId, copied: 'Copied Asset ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('DTC Asset'),
  component: DtcAssetDetail,
});

function getListItems(data: IxcDtccDtcV1AssetDayState | undefined) {
  const items = getDtcAssetDayStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.security.cusip, items.data.security.symbol.formatted, items.data.security.name, items.data.previous, items.data.next],
        },
        ...(data?.data?.opening
          ? [
              {
                id: 'opening',
                title: 'Opening',
                items: [items.data.opening.dtcParticipantBalance],
              },
            ]
          : []),
        ...(data?.data?.cnsInput
          ? [
              {
                id: 'cnsInput',
                title: 'CNS Input',
                items: [items.data.cnsInput.netSettlingQuantity],
              },
            ]
          : []),
        ...(data?.data?.apiBal
          ? [
              {
                id: 'apiBal',
                title: 'APIBal',
                items: [items.data.apiBal.date, items.data.apiBal.balance],
              },
            ]
          : []),
        ...(data?.data?.ragl
          ? [
              {
                id: 'ragl',
                title: 'RAGL',
                items: [items.data.ragl.asOf, items.data.ragl.dtcSettled],
              },
            ]
          : []),
      ] satisfies ReadonlyListGroup[],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.assetId, items.date] satisfies ReadonlyListItem[],
    },
  } as const;
}

function getMenuItems(assetId: string, date: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DtcAssetDayEventDrawer,
        props: { request: { assetId, date } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load DTC asset day', 'view this DTC asset day');

function DtcAssetDetail() {
  const { assetId, date } = Route.useParams();
  const request = { assetId, date } as const;
  const { data, error, isPending } = useIxcDtccDtcV1AssetDayQueryServiceAssetDayGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.assetDay), [data?.assetDay]);
  const menuItems = useMemo(() => getMenuItems(assetId, date), [assetId, date]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <DtcAssetDayStatusPill isPending={showPending} size="large" status={data?.assetDay?.status} />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <DtcAssetDayEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {data?.assetDay?.data?.dtcCaPayments?.length ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>DTC CA Payments</Typography.Level3>

            <DtcCaPaymentsTable payments={data.assetDay.data.dtcCaPayments} showSkeleton={showPending} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
